import { FormInstance } from 'antd';
import { Order } from 'constants/interface';
import { OrderOtherStatusEnum } from 'constants/order';
import { PaymentMethodEnum, PaymentMethodMapping } from 'constants/payment';
import dayjs, { Dayjs } from 'dayjs';
import { normalizeNullValues } from 'helpers/form';
import React, { useState } from 'react';
import {
  Collapse,
  DatePicker,
  Input,
  Select,
  Switch,
} from 'UI';

import {
  MainContainer,
  AdvanceInfoCollapse,
  FormContainer,
  HorizontalContainer,
  BaseFormItem,
} from './style';

export interface AdvanceInfoFormData {
  advancePaymentDate: Dayjs
  advancePaymentMethod: PaymentMethodEnum
  advancePaymentPrice: string
  advancePaymentResponsible: string
  advancePaymentConfirmed: boolean
}
interface AdvanceInfoProps {
  formHandler: FormInstance<AdvanceInfoFormData>
  isLoading: boolean
  initialOrder?: Order
  isCopy?: boolean
}

const AdvanceInfo = ({
  formHandler,
  isLoading: externalLoading,
  initialOrder,
  isCopy,
}: AdvanceInfoProps): JSX.Element => {
  const initialFillingRequired = initialOrder !== undefined
    && (
      !!initialOrder?.advancePaymentDate
      || !!initialOrder?.advancePaymentPrice
      || !!initialOrder?.advancePaymentResponsible
    );

  const [isFillingRequired, setFillingRequired] = useState<boolean>(initialFillingRequired);

  const advanceInfoChanged = () => {
    const fieldValues: AdvanceInfoFormData = formHandler.getFieldsValue();
    const {
      advancePaymentDate,
      advancePaymentPrice,
      advancePaymentResponsible,
    } = fieldValues;

    if (
      advancePaymentDate
      || advancePaymentPrice
      || advancePaymentResponsible
    ) {
      setFillingRequired(true);
    } else {
      setFillingRequired(false);
    }
  };

  const isActiveOrderCanceled = initialOrder?.status === OrderOtherStatusEnum.CANCELED;

  return (
    <MainContainer>
      <FormContainer
        layout="vertical"
        form={formHandler}
        validateTrigger="onSubmit"
        requiredMark={false}
        initialValues={{
          advancePaymentDate: initialOrder?.advancePaymentDate && !isCopy
            ? dayjs(initialOrder?.advancePaymentDate)
            : undefined,
          advancePaymentMethod: initialOrder?.advancePaymentMethod || PaymentMethodEnum.CASH,
          advancePaymentPrice: initialOrder?.advancePaymentPrice && !isCopy
            ? `${(initialOrder?.advancePaymentPrice) / 100}`
            : undefined,
          advancePaymentResponsible: (initialOrder && !isCopy)
            ? initialOrder?.advancePaymentResponsible || undefined
            : undefined,
          advancePaymentConfirmed: (initialOrder && !isCopy)
            ? initialOrder?.advancePaymentConfirmed
            : undefined,
        }}
      >
        <AdvanceInfoCollapse
          ghost={false}
          defaultActiveKey="ADVANCE_INFO"
        >

          <Collapse.Panel
            key="ADVANCE_INFO"
            header="Adiantamento"
            extra={(
              <BaseFormItem
                name="advancePaymentConfirmed"
                valuePropName="checked"
              >
                <Switch
                  onClick={(
                    checked,
                    event,
                  ) => {
                    event.stopPropagation();
                  }}
                  disabled={
                    (isCopy === true)
                      ? false
                      : externalLoading || isActiveOrderCanceled
                  }
                />
              </BaseFormItem>
            )}
          >
            <HorizontalContainer>
              <BaseFormItem
                name="advancePaymentDate"
                label="Data de recebimento"
                rules={[{
                  required: isFillingRequired,
                  message: 'Insira uma data',
                }]}
              >
                <DatePicker
                  disabled={externalLoading || isActiveOrderCanceled}
                  onChange={advanceInfoChanged}
                />
              </BaseFormItem>

              <BaseFormItem
                name="advancePaymentMethod"
                label="Meio de pagamento"
              >
                <Select
                  placeholder="Selecione"
                  notFoundContent={null}
                  disabled={externalLoading || isActiveOrderCanceled}
                >
                  {Object
                    .values(PaymentMethodEnum)
                    .map((curMethod) => (
                      <Select.Option
                        value={curMethod}
                        key={curMethod}
                      >
                        {PaymentMethodMapping[curMethod]}
                      </Select.Option>
                    ))}
                </Select>
              </BaseFormItem>

              <BaseFormItem
                name="advancePaymentPrice"
                label="Valor recebido"
                rules={[{
                  required: isFillingRequired,
                  message: 'Insira um valor',
                }]}
              >
                <Input
                  type="number"
                  prefix="R$"
                  disabled={externalLoading || isActiveOrderCanceled}
                  onChange={advanceInfoChanged}
                />
              </BaseFormItem>

              <BaseFormItem
                name="advancePaymentResponsible"
                label="Responsável"
                normalize={(value) => normalizeNullValues<string>(value)}
                rules={[{
                  required: isFillingRequired,
                  message: 'Insira um responsável',
                }]}
              >
                <Input
                  type="text"
                  disabled={externalLoading || isActiveOrderCanceled}
                  onChange={advanceInfoChanged}
                />
              </BaseFormItem>
            </HorizontalContainer>
          </Collapse.Panel>
        </AdvanceInfoCollapse>
      </FormContainer>
    </MainContainer>
  );
};

export default AdvanceInfo;
